import PartnershipSettlements from '@/components/admin/PartnershipSettlements';
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { TrendUp, TrendDown, Users, CalendarCheck, Warning, ChartBar, X, CalendarBlank, Broom, Wallet, CreditCard, Trophy, DownloadSimple } from '@phosphor-icons/react';
import { useNavigate, Link } from 'react-router-dom';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useRequireAdmin } from '@/hooks/useRequireAdmin';
import { fmtDate } from '@/utils/dateFmt';
import { fmtDate as fmtDatePt } from '@/utils/dateFmt';

const API = process.env.REACT_APP_BACKEND_URL;
const PERIODS = [
  { value: 'day', label: 'Dia' },
  { value: 'week', label: 'Semana' },
  { value: 'month', label: 'Mês' },
  { value: 'quarter', label: 'Trimestre' },
  { value: 'semester', label: 'Semestre' },
  { value: 'year', label: 'Ano' },
  { value: 'custom', label: 'Personalizado' },
];
const eur = (v) => `€${Number(v || 0).toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function MetricCard({ label, value, previous, icon: Icon, color = 'text-brand-green', onClick, formatValue = (v) => v }) {
  const diff = previous != null ? value - previous : null;
  const pctChange = previous ? Math.round(((value - previous) / Math.max(previous, 1)) * 100) : null;
  return (
    <div onClick={onClick} className={`bg-white border border-brand-border rounded-2xl p-5 ${onClick ? 'cursor-pointer hover:border-brand-green/40 hover:shadow-md transition-all' : ''}`} data-testid={`metric-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-brand-muted font-medium uppercase tracking-wider">{label}</span>
        {Icon && <Icon size={18} className="text-brand-placeholder" weight="duotone" />}
      </div>
      <p className={`text-3xl font-heading font-bold ${color}`}>{formatValue(value)}</p>
      {diff !== null && (
        <div className="flex items-center gap-1 mt-1.5">
          {diff >= 0 ? <TrendUp size={12} className="text-brand-green" /> : <TrendDown size={12} className="text-red-500" />}
          <span className={`text-[11px] font-medium ${diff >= 0 ? 'text-brand-green' : 'text-red-500'}`}>
            {diff >= 0 ? '+' : ''}{formatValue(diff)} ({pctChange >= 0 ? '+' : ''}{pctChange}%)
          </span>
          <span className="text-[10px] text-brand-muted">vs anterior</span>
        </div>
      )}
    </div>
  );
}

function DetailPanel({ title, items, onClose, navigate, archivable, onArchive }) {
  if (!items) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex justify-end" onClick={onClose}>
      <div className="w-full max-w-md bg-white h-full overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()} data-testid="detail-panel">
        <div className="p-5 border-b border-brand-border flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="font-heading font-medium text-lg text-brand-text">{title}</h3>
          <button onClick={onClose} className="p-1.5 text-brand-muted hover:text-brand-text"><X size={18} /></button>
        </div>
        <div className="p-4 space-y-2">
          {items.length === 0 ? <p className="text-sm text-brand-muted text-center py-8">Sem dados para este período.</p> : (
            items.map((item, i) => {
              const Inner = (
                <>
                  <p className="text-sm font-medium text-brand-text">{item.name || item.patient_name || '-'}</p>
                  {item.phone && (
                    <a href={`tel:${item.country_code || ''}${item.phone}`} className="text-[11px] text-brand-green font-medium no-underline hover:underline" data-testid={`ceo-phone-${item.user_id}`}>{item.country_code || ''} {item.phone}</a>
                  )}
                  <div className="flex gap-3 mt-0.5">
                    {item.patient_type && <span className="text-[10px] text-brand-muted">{item.patient_type}</span>}
                    {item.program && <span className="text-[10px] text-brand-muted">{item.program}</span>}
                    {item.date && <span className="text-[10px] text-brand-placeholder">{fmtDate(item.date)}</span>}
                    {item.created_at && <span className="text-[10px] text-brand-placeholder">{fmtDate(item.created_at)}</span>}
                    {item.end_date && <span className="text-[10px] text-amber-600">Termina: {fmtDatePt(item.end_date)}</span>}
                    {item.duration && <span className="text-[10px] text-brand-muted">{item.duration}</span>}
                  </div>
                </>
              );
              if (archivable && item.user_id) {
                return (
                  <div key={i} className="bg-brand-bg/30 border border-brand-border rounded-xl p-3" data-testid={`ceo-row-${item.user_id}`}>
                    <Link to={`/admin/pacientes/${item.user_id}`} className="block no-underline text-inherit hover:opacity-80">{Inner}</Link>
                    <button onClick={() => onArchive(item.user_id, item.name || item.patient_name)} className="mt-2 text-[10px] border border-brand-border text-brand-muted rounded-full px-2.5 py-1 hover:bg-red-50 hover:text-red-500 hover:border-red-200 transition-colors" data-testid={`archive-na-${item.user_id}`}>Arquivar (não aplicável)</button>
                  </div>
                );
              }
              return item.user_id ? (
                <Link key={i} to={`/admin/pacientes/${item.user_id}`} className="block bg-brand-bg/30 border border-brand-border rounded-xl p-3 cursor-pointer hover:border-brand-green/30 no-underline text-inherit" data-testid={`ceo-row-${item.user_id}`}>{Inner}</Link>
              ) : (
                <div key={i} className="bg-brand-bg/30 border border-brand-border rounded-xl p-3">{Inner}</div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}

export default function CeoDashboardPage() {
  useRequireAdmin();
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState('month');
  const [loading, setLoading] = useState(true);
  const [panel, setPanel] = useState(null); // { title, items }
  const navigate = useNavigate();

  const [satisfaction, setSatisfaction] = useState(null);
  const [noAptData, setNoAptData] = useState(null);
  const [noAptDays, setNoAptDays] = useState(30);
  const [overdueTasks, setOverdueTasks] = useState(null);
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [partnershipKpi, setPartnershipKpi] = useState(null);
  const [financials, setFinancials] = useState(null);

  const fetch = useCallback(async () => {
    setLoading(true);
    try {
      const params = period === 'custom' && customFrom && customTo
        ? `period=custom&date_from=${customFrom}&date_to=${customTo}`
        : `period=${period}`;
      const [dRes, sRes, nRes, tRes, pRes, fRes] = await Promise.all([
        axios.get(`${API}/api/ceo/dashboard?${params}`),
        axios.get(`${API}/api/ceo/satisfaction`),
        axios.get(`${API}/api/ceo/patients-without-appointment?days=${noAptDays}`),
        axios.get(`${API}/api/auto-tasks/overdue-summary`),
        axios.get(`${API}/api/admin/partnership-conversion-kpi?${params}`),
        axios.get(`${API}/api/ceo/dashboard/financials?${params}`),
      ]);
      setData(dRes.data); setSatisfaction(sRes.data); setNoAptData(nRes.data); setOverdueTasks(tRes.data); setPartnershipKpi(pRes.data); setFinancials(fRes.data);
    } catch {}
    finally { setLoading(false); }
  }, [period, customFrom, customTo, noAptDays]);
  useEffect(() => { fetch(); }, [fetch]);

  const [showBulk, setShowBulk] = useState(false);
  const [bulkMonths, setBulkMonths] = useState(12);
  const [bulkPreview, setBulkPreview] = useState(null); // {count, cutoff, candidates}
  const [bulkSelected, setBulkSelected] = useState({});
  const [bulkLoading, setBulkLoading] = useState(false);

  const openBulkCleanup = () => { setShowBulk(true); setBulkPreview(null); setBulkSelected({}); };
  const runBulkPreview = async () => {
    setBulkLoading(true);
    try {
      const { data } = await axios.get(`${API}/api/ceo/bulk-not-applicable/preview?months=${bulkMonths}`);
      setBulkPreview(data);
      const sel = {}; (data.candidates || []).forEach(c => { sel[c.user_id] = true; });
      setBulkSelected(sel);
    } catch { toast.error('Erro ao pré-visualizar'); } finally { setBulkLoading(false); }
  };
  const applyBulk = async () => {
    const ids = Object.keys(bulkSelected).filter(id => bulkSelected[id]);
    if (ids.length === 0) { toast.error('Seleciona pelo menos uma cliente'); return; }
    if (!window.confirm(`Arquivar ${ids.length} cliente(s) como "não aplicável"? Saem das listas activas e das métricas de retenção/churn.`)) return;
    setBulkLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/ceo/bulk-not-applicable/apply`, { user_ids: ids, months: Number(bulkMonths) || 12 });
      toast.success(`${data.archived} cliente(s) arquivada(s)`);
      setShowBulk(false); fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); } finally { setBulkLoading(false); }
  };
  const selectedCount = Object.values(bulkSelected).filter(Boolean).length;

  const handleArchiveNotApplicable = async (userId, name) => {    if (!window.confirm(`Arquivar ${name || 'esta cliente'} como "não aplicável"? Sai das listas activas e deixa de contar para retenção e churn.`)) return;
    try {
      await axios.post(`${API}/api/ceo/patients/${userId}/archive-not-applicable`);
      toast.success('Cliente arquivada e excluída das métricas');
      setPanel(p => p ? { ...p, items: p.items.filter(it => it.user_id !== userId) } : p);
      fetch();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  const exportFinancialsCsv = async () => {
    try {
      const params = period === 'custom' && customFrom && customTo
        ? `period=custom&date_from=${customFrom}&date_to=${customTo}`
        : `period=${period}`;
      const res = await axios.get(`${API}/api/ceo/dashboard/financials/csv?${params}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url; a.download = `ceo_financeiro_${period}.csv`; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Erro ao exportar CSV'); }
  };

  if (loading && !data) return <div className="text-center py-12 text-brand-muted">A carregar dashboard...</div>;

  return (
    <div className="fade-in" data-testid="ceo-dashboard-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="font-heading font-medium text-2xl text-brand-text">Dashboard CEO</h2>
          <p className="text-sm text-brand-muted mt-1">Visão geral do negócio</p>
        </div>
        <div className="flex gap-1.5">
          {PERIODS.map(p => (
            <button key={p.value} onClick={() => setPeriod(p.value)}
              className={`px-3 py-1.5 rounded-full text-xs font-medium ${period === p.value ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted border border-brand-border'}`}
              data-testid={`period-${p.value}`}>
              {p.label}
            </button>
          ))}
          {period === 'custom' && (
            <div className="flex gap-1 items-center">
              <Input type="date" value={customFrom} onChange={e => setCustomFrom(e.target.value)} className="rounded-lg border-brand-border text-xs w-36 h-7" />
              <span className="text-xs text-brand-muted">a</span>
              <Input type="date" value={customTo} onChange={e => setCustomTo(e.target.value)} className="rounded-lg border-brand-border text-xs w-36 h-7" />
            </div>
          )}
        </div>
      </div>

      {data && (
        <>
          {/* KPI Cards — clickable */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <MetricCard label="Novos clientes" value={data.new_clients.current} previous={data.new_clients.previous} icon={Users} onClick={() => setPanel({ title: 'Novos clientes', items: data.new_clients.list || [] })} />
            <MetricCard label="Consultas" value={data.consultations.current} previous={data.consultations.previous} icon={CalendarCheck} onClick={() => setPanel({ title: 'Consultas do período', items: data.consultations.list || [] })} />
            <MetricCard label="Cancelamentos" value={data.cancellations.current} previous={data.cancellations.previous} icon={Warning} color="text-red-500" onClick={() => setPanel({ title: 'Cancelamentos', items: data.cancellations.list || [] })} />
            <MetricCard label="Clientes ativos" value={data.active_clients_total} icon={ChartBar} />
          </div>

          {/* Financeiro (inspirado no dashboard Stripe) */}
          {financials && (
            <div className="mb-6" data-testid="financials-section">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading font-medium text-sm text-brand-muted uppercase tracking-wider flex items-center gap-2"><CreditCard size={16} /> Financeiro</h3>
                <button onClick={exportFinancialsCsv} className="flex items-center gap-1.5 text-xs border border-brand-border text-brand-muted rounded-full px-3 py-1.5 hover:border-brand-green hover:text-brand-green transition-colors" data-testid="export-financials-csv-btn">
                  <DownloadSimple size={13} /> Exportar CSV
                </button>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
                <div className="bg-white border border-brand-border rounded-2xl p-5" data-testid="stripe-balance-card">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs text-brand-muted font-medium uppercase tracking-wider">Saldo Stripe</span>
                    <Wallet size={18} className="text-brand-placeholder" weight="duotone" />
                  </div>
                  {financials.stripe_balance ? (
                    <>
                      <p className="text-2xl font-heading font-bold text-brand-green">{eur(financials.stripe_balance.available)}</p>
                      <p className="text-[11px] text-brand-muted mt-1">A repassar: {eur(financials.stripe_balance.pending_payout)}</p>
                    </>
                  ) : (
                    <p className="text-xs text-brand-placeholder mt-2">Sem ligação à Stripe Balance API</p>
                  )}
                </div>
                <MetricCard label="Volume bruto" value={financials.gross_volume.current} previous={financials.gross_volume.previous} icon={ChartBar} formatValue={eur} />
                <MetricCard label="Volume líquido" value={financials.net_volume.current} previous={financials.net_volume.previous} icon={ChartBar} formatValue={eur} />
                <div className="bg-white border border-brand-border rounded-2xl p-5" data-testid="payment-status-breakdown-card">
                  <span className="text-xs text-brand-muted font-medium uppercase tracking-wider">Pagamentos por estado</span>
                  <div className="mt-2.5 space-y-1.5">
                    {[
                      { key: 'concluido', label: 'Concluído', color: 'text-brand-green' },
                      { key: 'pendente', label: 'Pendente', color: 'text-amber-600' },
                      { key: 'reembolsado', label: 'Reembolsado', color: 'text-blue-600' },
                      { key: 'malsucedido', label: 'Malsucedido', color: 'text-red-500' },
                    ].map(s => (
                      <div key={s.key} className="flex items-center justify-between text-xs">
                        <span className={s.color}>{s.label}</span>
                        <span className="font-medium text-brand-text">{financials.status_breakdown[s.key].count} · {eur(financials.status_breakdown[s.key].total)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-white border border-brand-border rounded-2xl p-5" data-testid="top-clients-ranking-card">
                <h4 className="text-sm text-brand-muted font-medium uppercase tracking-wider mb-3 flex items-center gap-2"><Trophy size={15} /> Ranking de clientes por gasto</h4>
                {financials.top_clients.length === 0 ? <p className="text-xs text-brand-placeholder">Sem pagamentos concluídos neste período.</p> : (
                  <div className="space-y-2">
                    {financials.top_clients.map((c, i) => (
                      <div key={c.patient_id} className="flex items-center justify-between py-1.5 border-b border-brand-border/40 last:border-0" data-testid={`top-client-${i}`}>
                        <div className="flex items-center gap-2.5">
                          <span className="w-5 h-5 rounded-full bg-brand-bg text-[10px] font-semibold text-brand-muted flex items-center justify-center">{i + 1}</span>
                          {c.patient_id ? <Link to={`/admin/pacientes/${c.patient_id}`} className="text-sm text-brand-text no-underline hover:text-brand-green">{c.name}</Link> : <span className="text-sm text-brand-text">{c.name}</span>}
                        </div>
                        <span className="text-sm font-semibold text-brand-green">{eur(c.total)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Satisfaction + Financial Health */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            {satisfaction && satisfaction.total > 0 && (
              <div className="bg-white border border-brand-border rounded-2xl p-5">
                <h3 className="font-heading font-medium text-sm text-brand-muted uppercase tracking-wider mb-3">Satisfação</h3>
                <div className="flex items-center gap-6">
                  <div className="text-center"><p className="text-3xl font-heading font-bold text-brand-green">{satisfaction.average}</p><p className="text-[10px] text-brand-muted">Média</p></div>
                  <div className="text-center"><p className="text-2xl font-heading font-bold text-brand-text">{satisfaction.total}</p><p className="text-[10px] text-brand-muted">Feedbacks</p></div>
                  <div className="text-center"><p className="text-2xl font-heading font-bold text-brand-green">{satisfaction.positive_pct}%</p><p className="text-[10px] text-brand-muted">Positivos</p></div>
                </div>
              </div>
            )}
          </div>

          {/* Program breakdown + Partnerships + Retention */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
            <div className="bg-white border border-brand-border rounded-2xl p-5" data-testid="by-program-card">
              <h3 className="font-heading font-medium text-sm text-brand-muted uppercase tracking-wider mb-4">Clientes por programa</h3>
              {Object.keys(data.by_program).length === 0 ? <p className="text-xs text-brand-placeholder">Sem dados.</p> : (
                <div className="space-y-3">
                  {Object.entries(data.by_program).sort((a, b) => b[1] - a[1]).map(([program, count]) => (
                    <div key={program} className="flex items-center justify-between">
                      <span className="text-sm text-brand-text">{program}</span>
                      <div className="flex items-center gap-2">
                        <div className="w-24 h-2 bg-brand-bg rounded-full overflow-hidden">
                          <div className="h-full bg-brand-green rounded-full" style={{ width: `${Math.min(100, (count / Math.max(data.active_clients_total, 1)) * 100)}%` }} />
                        </div>
                        <span className="text-sm font-semibold text-brand-text w-8 text-right">{count}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {data.partnerships?.length > 0 && (
                <div className="mt-4 pt-4 border-t border-brand-border/50">
                  <h4 className="text-xs text-purple-600 font-medium uppercase tracking-wider mb-3">Parcerias</h4>
                  <div className="space-y-2">
                    {data.partnerships.map(p => (
                      <div key={p.partner_name} className="bg-purple-50/50 border border-purple-100 rounded-xl p-3">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-brand-text">{p.partner_name}</span>
                          <span className="text-xs text-purple-600 font-semibold">{p.clients} clientes</span>
                        </div>
                        <div className="flex gap-4 text-[10px] text-brand-muted">
                          <span>{p.consultations} consultas</span>
                          {p.percentage > 0 && <span>{p.percentage}% nutri</span>}
                          {p.payment_handler && <span>Pago: {p.payment_handler}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <PartnershipSettlements />
            </div>

            {/* Retention + Risk */}
            <div className="bg-white border border-brand-border rounded-2xl p-5" data-testid="retention-card">
              <h3 className="font-heading font-medium text-sm text-brand-muted uppercase tracking-wider mb-4">Retenção e risco</h3>
              <div className="flex items-baseline gap-2 mb-4">
                <span className="text-4xl font-heading font-bold text-brand-green">{data.retention_rate}%</span>
                <span className="text-sm text-brand-muted">taxa de retenção</span>
              </div>
              <button onClick={() => setPanel({ title: 'Clientes em risco de abandono', items: data.at_risk || [], archivable: true })} className="w-full flex items-center gap-2 mb-3 p-3 bg-amber-50 border border-amber-200 rounded-xl hover:bg-amber-100 transition-colors cursor-pointer" data-testid="at-risk-btn">
                <Warning size={16} className="text-amber-600 flex-shrink-0" />
                <span className="text-xs text-amber-800 font-medium">{data.at_risk_count} cliente{data.at_risk_count !== 1 ? 's' : ''} em risco de abandono</span>
              </button>
              {data.low_mood_alerts?.length > 0 && (
                <button onClick={() => setPanel({ title: 'Humor baixo prolongado (≥2 semanas)', items: (data.low_mood_alerts || []).map(a => ({ user_id: a.patient_id, name: a.name, patient_type: `${a.challenge || 'Desafio'} · ${a.weeks} sem. · média ${a.avg_mood ?? '-'}${a.nutri_name ? ' · ' + a.nutri_name : ''}` })) })} className="w-full flex items-center gap-2 mb-3 p-3 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 transition-colors cursor-pointer" data-testid="low-mood-btn">
                  <Warning size={16} className="text-red-600 flex-shrink-0" />
                  <span className="text-xs text-red-800 font-medium text-left">{data.low_mood_alerts.length} participante{data.low_mood_alerts.length !== 1 ? 's' : ''} com humor baixo há ≥2 semanas</span>
                </button>
              )}
              <button onClick={openBulkCleanup} className="w-full flex items-center gap-2 mb-3 p-3 bg-brand-bg border border-brand-border rounded-xl hover:bg-brand-border/30 transition-colors cursor-pointer" data-testid="bulk-cleanup-btn">
                <Broom size={16} className="text-brand-muted flex-shrink-0" />
                <span className="text-xs text-brand-text font-medium">Limpeza de legado (sem consulta há muito tempo)</span>
              </button>
              {data.expiring_programs?.length > 0 && (
                <div className="mb-3">
                  <button onClick={() => setPanel({ title: 'Programas a terminar em 14 dias', items: data.expiring_programs || [] })} className="w-full flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-xl hover:bg-blue-100 transition-colors cursor-pointer mb-1.5" data-testid="expiring-btn">
                    <ChartBar size={16} className="text-blue-600 flex-shrink-0" />
                    <span className="text-xs text-blue-800 font-medium">{data.expiring_programs.length} programa{data.expiring_programs.length !== 1 ? 's' : ''} a terminar em 14 dias</span>
                  </button>
                  <div className="flex gap-1">
                    {[7, 15, 30].map(d => {
                      const filtered = (data.expiring_programs || []).filter(p => { const days = Math.ceil((new Date(p.end_date) - new Date()) / 86400000); return days <= d && days >= 0; });
                      return filtered.length > 0 ? (
                        <button key={d} onClick={() => setPanel({ title: `Programas a terminar em ${d} dias`, items: filtered })} className="text-[9px] bg-blue-50 text-blue-600 px-2 py-1 rounded-full border border-blue-100 hover:bg-blue-100">{filtered.length} em {d}d</button>
                      ) : null;
                    })}
                  </div>
                </div>
              )}
              {/* Patients without appointment */}
              {noAptData && (
                <div className="mt-3 pt-3 border-t border-brand-border/50">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-xs font-medium text-brand-muted">Sem consulta agendada</span>
                    <div className="flex gap-1">
                      {[7, 14, 30, 60].map(d => (
                        <button key={d} onClick={() => setNoAptDays(d)} className={`text-[9px] px-2 py-0.5 rounded-full ${noAptDays === d ? 'bg-brand-green text-white' : 'bg-brand-bg text-brand-muted'}`}>{d}d</button>
                      ))}
                    </div>
                  </div>
                  {noAptData.no_future_appointment?.length > 0 && (
                    <button onClick={() => setPanel({ title: 'Sem consulta futura agendada', items: noAptData.no_future_appointment })} className="w-full flex items-center gap-2 mb-1.5 p-2.5 bg-red-50 border border-red-200 rounded-xl hover:bg-red-100 text-left">
                      <CalendarBlank size={14} className="text-red-500 flex-shrink-0" />
                      <span className="text-[11px] text-red-700 font-medium">{noAptData.no_future_appointment.length} sem consulta futura</span>
                    </button>
                  )}
                  {noAptData.no_recent_consultation?.length > 0 && (
                    <button onClick={() => setPanel({ title: `Sem consulta há mais de ${noAptDays} dias`, items: noAptData.no_recent_consultation })} className="w-full flex items-center gap-2 p-2.5 bg-amber-50 border border-amber-200 rounded-xl hover:bg-amber-100 text-left">
                      <Warning size={14} className="text-amber-600 flex-shrink-0" />
                      <span className="text-[11px] text-amber-700 font-medium">{noAptData.no_recent_consultation.length} sem consulta há {noAptDays}+ dias</span>
                    </button>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Tarefas automáticas em atraso, por nutricionista */}
          {overdueTasks && overdueTasks.some(t => t.overdue_count > 0) && (
            <div className="bg-white border border-brand-border rounded-2xl p-5 mb-6" data-testid="ceo-overdue-tasks-card">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-heading text-sm text-brand-text">Tarefas Automáticas em Atraso</h3>
                <Link to="/admin/tarefas" className="text-[11px] text-brand-green hover:underline">Ver todas</Link>
              </div>
              <div className="flex gap-3">
                {overdueTasks.filter(t => t.overdue_count > 0).map(t => (
                  <div key={t.nutri_id} className="flex-1 bg-amber-50 border border-amber-200 rounded-xl p-3 text-center" data-testid={`overdue-tasks-${t.nutri_id}`}>
                    <p className="text-2xl font-heading font-bold text-amber-700">{t.overdue_count}</p>
                    <p className="text-[10px] text-amber-700 truncate">{t.name}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Consultas por profissional (Ana vs Filipa) — Métrica 10 */}
          {data.consults_by_nutri?.length > 0 && (
            <div className="bg-white border border-brand-border rounded-2xl p-5 mb-6" data-testid="ceo-consults-by-nutri">
              <h3 className="font-heading font-medium text-sm text-brand-muted uppercase tracking-wider mb-4">Consultas por profissional</h3>
              <div className="space-y-3">
                {(() => { const max = Math.max(1, ...data.consults_by_nutri.map(n => n.total)); return data.consults_by_nutri.map(n => (
                  <div key={n.nutri_id} className="flex items-center gap-3">
                    <span className="w-28 shrink-0 text-sm text-brand-text truncate">{n.name}</span>
                    <div className="flex-1 h-5 bg-brand-bg rounded-full overflow-hidden">
                      <div className="h-full bg-brand-green rounded-full flex items-center justify-end pr-2" style={{ width: `${Math.max(8, (n.total / max) * 100)}%` }}>
                        <span className="text-[10px] font-semibold text-white">{n.total}</span>
                      </div>
                    </div>
                    <span className="w-24 shrink-0 text-right text-[11px] text-brand-muted">{n.weekly_avg}/semana</span>
                  </div>
                )); })()}
              </div>
            </div>
          )}

          {/* Conversão por Parceria (Sessões Gratuitas) — P5 Marketing */}
          {partnershipKpi?.partnerships?.length > 0 && (
            <div className="bg-white border border-brand-border rounded-2xl p-5 mb-6" data-testid="ceo-partnership-conversion-card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-heading font-medium text-sm text-brand-muted uppercase tracking-wider">Conversão por Parceria</h3>
                <Link to="/admin/marketing/sessoes-gratuitas" className="text-[11px] text-brand-green hover:underline">Ver Sessões Gratuitas</Link>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {partnershipKpi.partnerships.map(p => (
                  <div key={p.code} className="bg-brand-bg/50 rounded-xl p-3.5" data-testid={`partnership-kpi-${p.code}`}>
                    <p className="text-sm font-medium text-brand-text">{p.code} <span className="text-brand-muted text-xs">· {p.partner_name}</span></p>
                    <div className="flex items-baseline gap-1.5 mt-1.5">
                      <span className="text-2xl font-heading font-bold text-brand-green">{p.conversion_rate}%</span>
                      <span className="text-[11px] text-brand-muted">conversão</span>
                    </div>
                    <p className="text-[11px] text-brand-muted mt-1">{p.sessions} sessão(ões) · {p.conversions} converteu(ram)</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}

      {/* Detail panel */}
      {panel && <DetailPanel title={panel.title} items={panel.items} onClose={() => setPanel(null)} navigate={navigate} archivable={panel.archivable} onArchive={handleArchiveNotApplicable} />}

      {/* Limpeza de legado em massa — pré-visualizar antes de arquivar */}
      <Dialog open={showBulk} onOpenChange={setShowBulk}>
        <DialogContent className="rounded-3xl border-brand-border max-w-lg" data-testid="bulk-cleanup-dialog">
          <DialogHeader><DialogTitle className="font-heading text-brand-text">Limpeza de legado</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-brand-muted">Lista as clientes activas sem consulta há mais do que o limiar definido. Revê e desmarca quem quiseres manter antes de arquivar.</p>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <label className="text-[11px] text-brand-muted mb-1 block">Sem consulta há mais de (meses)</label>
                <Input type="number" min="1" max="60" value={bulkMonths} onChange={e => setBulkMonths(e.target.value)} className="rounded-xl border-brand-border" data-testid="bulk-months-input" />
              </div>
              <button onClick={runBulkPreview} disabled={bulkLoading} className="bg-brand-green text-white rounded-full px-4 py-2 text-sm font-medium disabled:opacity-50" data-testid="bulk-preview-btn">{bulkLoading ? 'A carregar...' : 'Pré-visualizar'}</button>
            </div>

            {bulkPreview && (
              <div data-testid="bulk-preview-result">
                <div className="flex items-center justify-between py-2 border-y border-brand-border/50">
                  <p className="text-xs text-brand-text"><b>{bulkPreview.count}</b> cliente(s) sem consulta desde antes de {bulkPreview.cutoff}</p>
                  {bulkPreview.count > 0 && <span className="text-[11px] text-brand-muted">{selectedCount} selecionada(s)</span>}
                </div>
                {bulkPreview.count === 0 ? (
                  <p className="text-xs text-brand-muted text-center py-6">Nenhuma cliente cumpre este limiar. Reduz os meses para ver mais.</p>
                ) : (
                  <ul className="max-h-72 overflow-y-auto space-y-1 mt-2">
                    {bulkPreview.candidates.map(c => (
                      <li key={c.user_id} className="flex items-center gap-2 px-2 py-1.5 rounded-lg hover:bg-brand-bg/50" data-testid={`bulk-candidate-${c.user_id}`}>
                        <input type="checkbox" checked={!!bulkSelected[c.user_id]} onChange={e => setBulkSelected(s => ({ ...s, [c.user_id]: e.target.checked }))} className="rounded" data-testid={`bulk-check-${c.user_id}`} />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-brand-text truncate">{c.name}</p>
                          <p className="text-[10px] text-brand-muted">{c.program} · {c.never_consulted ? 'Nunca teve consulta' : `Última: ${c.last_consultation}`}{c.months_since != null ? ` · ${c.months_since} meses` : ''}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
                {bulkPreview.count > 0 && (
                  <button onClick={applyBulk} disabled={bulkLoading || selectedCount === 0} className="w-full mt-3 bg-red-500 text-white rounded-full py-2.5 text-sm font-medium disabled:opacity-50" data-testid="bulk-apply-btn">Arquivar {selectedCount} selecionada(s) como não aplicável</button>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
